<template>
  <div class="page-container portal-page">
    <portal to="page-name">Portal Stenci</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Portal Stenci</h1>
      </div>
      <div class="card-body">
        <template v-if="!canAccess">
          <div class="empty mt-2">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'lock']" size="3x"/>
            </div>
            <p class="empty-title h5">Acesso Negado</p>
            <p class="empty-subtitle">
              Você não possui permissão para gerenciar o Portal Stenci.
            </p>
          </div>
        </template>
        <div v-else>
          <st-tabs ref="portal-tabs">
            <st-tab id="tab-data" name="Indicadores">
              <div class="columns">
                <div class="column col-3 col-md-6 col-sm-12 form-group">
                  <label class="form-label">Mês</label>
                  <select
                    class="form-select"
                    v-model="filter.statsMonth"
                    @change="loadStats"
                  >
                    <option
                      v-for="month in months"
                      :value="month.value"
                      :key="month.month"
                    >{{ month.label }}</option>
                  </select>
                </div>
                <div class="column col-2 col-md-6 col-sm-12 form-group">
                  <label class="form-label">Ano</label>
                  <select
                    class="form-select"
                    v-model="filter.statsYear"
                    @change="loadStats"
                  >
                    <option
                      v-for="year in years"
                      :value="year.value"
                      :key="year.month"
                    >{{ year.label }}</option>
                  </select>
                </div>
              </div>

              <div class="loading loading-lg mt-2" v-if="statsLoading" />
              <template v-else>
                <div class="card chart-card">
                  <div class="card-body">
                    <div class="stenci-title mb-2">Agendamentos / visitas por dia</div>
                    <st-bar-chart
                      :chart-data="charts.appointments.data"
                      :options="charts.appointments.options"
                      :height="250"
                    />
                  </div>
                </div>

                <div class="divider" />

                <div class="card chart-card">
                  <div class="card-body">
                    <div class="stenci-title mb-2">Agendamentos por especialidade</div>
                    <st-bar-chart
                      :chart-data="charts.specialties.data"
                      :options="charts.specialties.options"
                      :height="250"
                    />
                  </div>
                </div>

                <div class="divider" />

                <div class="card chart-card">
                  <div class="card-body">
                    <div class="stenci-title mb-2">Agendamentos por plano de saúde</div>
                    <st-bar-chart
                      :chart-data="charts.insurances.data"
                      :options="charts.insurances.options"
                      :height="250"
                    />
                  </div>
                </div>
              </template>
            </st-tab>
            <st-tab id="tab-report" name="Relatório">
              <div class="columns">
                <div class="column col-3 col-md-6 col-sm-12 form-group">
                  <label class="form-label">Mês</label>
                  <select
                    class="form-select"
                    v-model="filter.appointmentMonth"
                    @change="loadAppointments"
                  >
                    <option
                      v-for="month in months"
                      :value="month.value"
                      :key="month.month"
                    >{{ month.label }}</option>
                  </select>
                </div>
                <div class="column col-2 col-md-6 col-sm-12 form-group">
                  <label class="form-label">Ano</label>
                  <select
                    class="form-select"
                    v-model="filter.appointmentYear"
                    @change="loadAppointments"
                  >
                    <option
                      v-for="year in years"
                      :value="year.value"
                      :key="year.month"
                    >{{ year.label }}</option>
                  </select>
                </div>
              </div>
              <div class="loading loading-lg mt-2" v-if="appointmentLoading" />
              <template v-else>
                <div class="empty" v-if="appointments.length === 0">
                  <div class="empty-icon">
                    <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                  </div>
                  <p class="empty-title h5">Agendamentos</p>
                  <p class="empty-subtitle">
                    Nenhum agendamento encontrado. Verifique os filtros para realizar novas buscas
                  </p>
                </div>
                <table class="table table-striped table-hover" v-else>
                  <thead>
                  <tr>
                    <th>#</th>
                    <th style="width: 140px">Data e hora</th>
                    <th>Agenda (Profissional)</th>
                    <th>Plano de saúde</th>
                    <th>Procedimento</th>
                    <th>Paciente</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, i) in appointments" :key="i">
                    <td>{{ i + 1 }}</td>
                    <td>{{ item.createdAt | date('datetime') }}</td>
                    <td class="text-ellipsis" style="max-width: 300px">
                      {{ item.schedule.name }} ({{ item.professional.name }})
                    </td>
                    <td class="text-ellipsis" style="max-width: 200px">
                      {{ item.insurance.name }}
                    </td>
                    <td class="text-ellipsis" style="max-width: 200px">
                      {{ item.expense.name }}
                    </td>
                    <td class="text-ellipsis" style="max-width: 150px">
                      {{ item.patient.name }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </template>
            </st-tab>
            <st-tab id="tab-config" name="Configurações">
              <div class="stenci-group stenci-alert">
                Nesta seção você controla o funcionamento do Portal Stenci,
                podendo ativar ou desativar o agendamento online, a entrega de
                resultados e administrar planos, serviços e profissionais vinculados.
              </div>

              <div class="loading loading-lg" v-if="loading" />
              <div v-else class="portal-config">
                <setting-item
                  class="first-item"
                  name="Ativar portal"
                  description="Publica o endereço do portal e libera para os seus pacientes
                    os recursos escolhidos abaixo."
                  type="switch"
                  v-model="isActive"
                />

                <div
                  class="stenci-group stenci-alert portal-disable"
                  v-if="!isActive && data.slug"
                >
                  <div class="portal-disable-text">
                    <div class="text-bold">Desativar o portal</div>
                    <div class="text-small">
                      O endereço <strong>{{ publishedUrl }}</strong> deixa de responder
                      para os pacientes. As unidades, planos, serviços e profissionais
                      vinculados continuam salvos para quando você reativar.
                    </div>
                  </div>
                  <button
                    class="btn btn-error btn-icon btn-icon-left"
                    :class="{loading: deleting}"
                    :disabled="deleting"
                    @click="remove"
                  >
                    <fa-icon :icon="['fal', 'times']"/>Confirmar desativação
                  </button>
                </div>

                <div v-if="isActive">
                  <div class="divider" />

                  <div class="stenci-title h6">Endereço do portal</div>

                  <div class="columns mt-2">
                    <div class="column col-4 col-md-6 col-sm-12 form-group"
                         :class="{'has-error': $v.data.slug.$error}">
                      <label class="form-label">Slug da URL</label>
                      <input
                        type="text"
                        class="form-input"
                        placeholder="nome-da-clinica"
                        maxlength="50"
                        v-model="data.slug"
                        @blur="formatSlugBlur"
                      >
                      <div class="form-input-hint" v-if="$v.data.slug.$error">
                        Campo obrigatório
                      </div>
                      <div
                        class="form-input-hint text-warning"
                        v-else-if="data.slug && !isPublished"
                      >
                        Salve as alterações para publicar este endereço.
                      </div>
                    </div>
                    <div class="column col-2 col-md-4 col-sm-12 form-group"
                         :class="{'has-error': $v.data.themeColor.$error}">
                      <label class="form-label">Cor do tema</label>
                      <select class="form-select" v-model="data.themeColor">
                        <option value="blue">Azul</option>
                        <option value="slate">Cinza</option>
                        <option value="brown">Marrom</option>
                        <option value="pink">Rosa</option>
                        <option value="purple">Roxo</option>
                        <option value="green">Verde</option>
                      </select>
                    </div>
                  </div>

                  <div class="divider" />

                  <div class="stenci-title h6">Recursos do portal</div>
                  <div
                    class="portal-resources"
                    :class="{'has-error': !$v.data.features}"
                  >
                    <setting-item
                      class="first-item"
                      name="Entrega de resultados"
                      description="Libera a área restrita do portal para o paciente acessar
                        os laudos dos exames dele."
                      type="switch"
                      v-model="data.resultsEnabled"
                    />
                    <div
                      class="portal-resource-link"
                      v-if="data.resultsEnabled && data.slug"
                    >
                      <div class="portal-resource-link-label">
                        <fa-icon :icon="['fal', 'link']"/>Link da área restrita
                      </div>
                      <div class="input-group">
                        <input class="form-input" type="text" :value="resultsUrl" readonly>
                        <template v-if="isPublished">
                          <button
                            class="btn input-group-btn btn-info btn-icon tooltip"
                            data-tooltip="Copiar"
                            @click="copyUrl(resultsUrl)"
                          ><fa-icon :icon="['fal', 'copy']"/></button>
                          <a
                            class="btn input-group-btn btn-icon tooltip"
                            data-tooltip="Abrir"
                            :href="resultsUrl"
                            target="_blank"
                            rel="noopener"
                          ><fa-icon :icon="['fal', 'external-link']"/></a>
                        </template>
                      </div>
                    </div>

                    <setting-item
                      name="Portal de agendamentos"
                      description="O paciente escolhe unidade, profissional e horário e marca
                        a consulta sozinho, a qualquer hora."
                      info="Custo de R$ 10,00 por agenda disponibilizada no portal."
                      type="switch"
                      v-model="data.schedulingEnabled"
                    />
                    <div
                      class="portal-resource-link"
                      v-if="data.schedulingEnabled && data.slug"
                    >
                      <div class="portal-resource-link-label">
                        <fa-icon :icon="['fal', 'link']"/>Link do agendamento
                      </div>
                      <div class="input-group">
                        <input class="form-input" type="text" :value="portalUrl" readonly>
                        <template v-if="isPublished">
                          <button
                            class="btn input-group-btn btn-info btn-icon tooltip"
                            data-tooltip="Copiar"
                            @click="copyUrl(portalUrl)"
                          ><fa-icon :icon="['fal', 'copy']"/></button>
                          <a
                            class="btn input-group-btn btn-icon tooltip"
                            data-tooltip="Abrir"
                            :href="portalUrl"
                            target="_blank"
                            rel="noopener"
                          ><fa-icon :icon="['fal', 'external-link']"/></a>
                        </template>
                      </div>
                    </div>
                    <div class="form-input-hint" v-if="!$v.data.features">
                      Selecione ao menos um recurso do portal para poder salvar.
                    </div>
                  </div>

                  <div class="divider" />

                  <div class="stenci-title h6">Relacionamentos</div>
                  <div class="stenci-group text-small" v-if="data.schedulingEnabled">
                    Os planos, serviços ou profissionais vinculados serão exibidos
                    no portal apenas quando possuírem agendas disponíveis e
                    tabelas de valores devidamente configuradas.
                  </div>
                  <div class="stenci-group text-small" v-else>
                    Com o agendamento online desativado, apenas as unidades
                    selecionadas serão exibidas no portal.
                  </div>

                  <st-tabs ref="portal-tabs" :key="`tabs-${data.schedulingEnabled}`">
                    <st-tab id="tab-branches" name="Unidades">
                      <div class="empty mt-2" v-if="branches.length === 0">
                        <div class="empty-icon">
                          <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                        </div>
                        <p class="empty-title h5">Unidades</p>
                        <p class="empty-subtitle">
                          Nenhuma unidade cadastrada no sistema
                        </p>
                      </div>
                      <table class="table table-hover" v-else>
                        <tr>
                          <th>#</th>
                          <th>Nome</th>
                          <th style="width: 40px">Status</th>
                        </tr>
                        <tr v-for="(item, i) in branches" :key="i">
                          <td>{{ i + 1 }}</td>
                          <td>{{ item.name }}</td>
                          <td>
                            <label class="form-switch text-center">
                              <input
                                type="checkbox"
                                :disabled="item.isOwner"
                                v-model="item.active"
                                @change="changeBranchStatus"
                              />
                              <i class="form-icon"/>
                            </label>
                          </td>
                        </tr>
                      </table>
                    </st-tab>
                    <st-tab
                      v-if="data.schedulingEnabled"
                      id="tab-insurances"
                      name="Planos de saúde"
                    >
                      <div class="loading loading-lg mt-2" v-if="itemsLoading" />
                      <template v-else>
                        <div class="empty mt-2" v-if="insurances.length === 0">
                          <div class="empty-icon">
                            <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                          </div>
                          <p class="empty-title h5">Planos de saúde</p>
                          <p class="empty-subtitle">
                            Nenhum plano cadastrado no sistema
                          </p>
                        </div>
                        <table class="table table-hover" v-else>
                          <tr>
                            <th>#</th>
                            <th>Nome interno</th>
                            <th>Nome externo</th>
                            <th>Status</th>
                          </tr>
                          <tr v-for="(item, i) in insurances" :key="i">
                            <td style="width: 40px">{{ i + 1 }}</td>
                            <td style="max-width: 200px">{{ item.name }}</td>
                            <td>
                              <input
                                type="text"
                                class="form-input input-sm"
                                :disabled="!item.active"
                                v-model="item.customName"
                                placeholder="Informe o nome externo"
                              >
                            </td>
                            <td style="width: 40px">
                              <label class="form-switch text-center">
                                <input
                                  type="checkbox"
                                  @change="changeInsuranceOrExpenseStatus(item)"
                                  v-model="item.active"
                                />
                                <i class="form-icon"/>
                              </label>
                            </td>
                          </tr>
                        </table>
                      </template>
                    </st-tab>
                    <st-tab v-if="data.schedulingEnabled" id="tab-expenses" name="Consultas">
                      <div class="loading loading-lg mt-2" v-if="itemsLoading" />
                      <template v-else>
                        <div class="empty mt-2" v-if="expenses.length === 0">
                          <div class="empty-icon">
                            <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                          </div>
                          <p class="empty-title h5">Serviços</p>
                          <p class="empty-subtitle">
                            Nenhum serviço cadastrado no sistema
                          </p>
                        </div>
                        <table class="table table-hover" v-else>
                          <tr>
                            <th>#</th>
                            <th>Nome interno</th>
                            <th>Nome externo</th>
                            <th>Status</th>
                          </tr>
                          <tr v-for="(item, i) in expenses" :key="i">
                            <td style="width: 40px">{{ i + 1 }}</td>
                            <td style="max-width: 200px">{{ item.name }}</td>
                            <td>
                              <input
                                type="text"
                                class="form-input input-sm"
                                :disabled="!item.active"
                                v-model="item.customName"
                                placeholder="Informe o nome externo"
                              >
                            </td>
                            <td style="width: 40px">
                              <label class="form-switch text-center">
                                <input
                                  type="checkbox"
                                  @change="changeInsuranceOrExpenseStatus(item)"
                                  v-model="item.active"
                                />
                                <i class="form-icon"/>
                              </label>
                            </td>
                          </tr>
                        </table>
                      </template>
                    </st-tab>
                    <st-tab
                      v-if="data.schedulingEnabled"
                      id="tab-professionals"
                      name="Profissionais"
                    >
                      <div class="loading loading-lg mt-2" v-if="itemsLoading" />
                      <template v-else>
                        <div class="empty mt-2" v-if="professionals.length === 0">
                          <div class="empty-icon">
                            <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
                          </div>
                          <p class="empty-title h5">Profissionais</p>
                          <p class="empty-subtitle">
                            Nenhum profissional cadastrado no sistema
                          </p>
                        </div>
                        <table class="table table-hover" v-else>
                          <tr>
                            <th>#</th>
                            <th>Nome</th>
                            <th>Status</th>
                          </tr>
                          <tr v-for="(item, i) in professionals" :key="i">
                            <td style="width: 40px">{{ i + 1 }}</td>
                            <td>
                              {{ item.name }}
                              <small class="text-secondary text-bold" v-if="item?.specialty?.name">
                                ({{ item.specialty.name }})
                              </small>
                            </td>
                            <td style="width: 40px">
                              <label class="form-switch text-center">
                                <input
                                  type="checkbox"
                                  v-model="item.active"
                                />
                                <i class="form-icon"/>
                              </label>
                            </td>
                          </tr>
                        </table>
                      </template>
                    </st-tab>
                  </st-tabs>

                  <div class="divider" />

                  <div class="portal-actions">
                    <button
                      class="btn btn-primary btn-icon btn-icon-left"
                      :class="{loading: saving}"
                      :disabled="saving"
                      @click="save"
                    >
                      <fa-icon :icon="['fal', 'save']"/>Salvar alterações
                    </button>
                  </div>
                </div>
              </div>
            </st-tab>
          </st-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { CREATE_PORTAL } from '@/data/actions/modules/general';
import { customName } from 'src/helpers/insurance';
import SettingItem from '../../setting/components/SettingItem.vue';

export default {
  components: {
    SettingItem,
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      isActive: false,
      itemsLoading: false,
      statsLoading: false,
      appointmentLoading: false,
      customName,
      savedSlug: null,
      data: this.blankData(),
      filter: {
        statsMonth: moment().format('MM'),
        statsYear: moment().format('YYYY'),
        appointmentMonth: moment().format('MM'),
        appointmentYear: moment().format('YYYY'),
      },
      months: [],
      years: [],
      branches: [],
      insurances: [],
      expenses: [],
      professionals: [],
      appointments: [],
      charts: {
        appointments: this.chartBase(),
        specialties: this.chartBase(),
        insurances: this.chartBase(),
      },
    };
  },
  computed: {
    canAccess() {
      return this.$can(CREATE_PORTAL);
    },
    portalUrl() {
      return `https://portal.stenci.app/${this.data.slug || ''}`;
    },
    resultsUrl() {
      return `${this.portalUrl}/app/my-exams`;
    },
    publishedUrl() {
      return this.savedSlug ? `https://portal.stenci.app/${this.savedSlug}` : null;
    },
    isPublished() {
      return !!this.savedSlug && this.savedSlug === this.data.slug;
    },
  },
  async mounted() {
    if (!this.canAccess) return;

    this.loadFilters();

    await Promise.all([
      this.load(),
      this.loadBranches(),
      this.loadStats(),
      this.loadAppointments(),
    ]);
    await this.loadSettings();
    await this.loadItems();

    this.loading = false;
  },
  watch: {
    'data.slug': function formatSlug(val) {
      if (!val) return;

      const formatted = (val ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z -]/g, '')
        .replace(/ +/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+/, '');

      if (formatted !== val) this.data.slug = formatted;
    },
  },
  validations: {
    data: {
      slug: { required },
      themeColor: { required },
      features: value => value.schedulingEnabled || value.resultsEnabled,
    },
  },
  methods: {
    load() {
      this.loading = true;

      return this.$http.get('/portal/identity')
        .then(({ data }) => {
          this.data.id = data.id;
          this.data.slug = data.slug;
          this.data.themeColor = data.themeColor;
          this.data.schedulingEnabled = data.schedulingEnabled === true;
          this.data.resultsEnabled = data.resultsEnabled === true;
          this.savedSlug = data.slug;
          this.isActive = !!data.slug;
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadBranches() {
      return this.$http.get('/portal/branches')
        .then(({ data }) => {
          this.branches = data.items.map(item => ({
            id: item.id,
            name: item.name,
            isOwner: item.isOwner,
            active: item.isOwner,
          }));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    loadSettings() {
      return this.$http.get('/portal/settings')
        .then(({ data }) => {
          this.data.branches = data.branches;
          this.data.insurances = data.insurances;
          this.data.expenses = data.expenses;
          this.data.professionals = data.professionals;

          this.data.branches.forEach((item) => {
            const found = this.branches.find(({ id }) => id === item.id);
            if (found) found.active = true;
          });
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    loadInsurances(branchIds) {
      if (!branchIds || !branchIds.length === 0 || this.insurances.length > 0) return null;

      this.itemsLoading = true;

      const params = {
        branchIds: branchIds.join(','),
      };

      return this.$http.get('/portal/insurances', { params })
        .then(({ data }) => {
          this.insurances = data.items.map(item => ({
            ids: item.ids,
            planIds: item.plan.ids,
            name: customName(item),
            customName: null,
            active: false,
          }));
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.itemsLoading = false;
        });
    },
    loadExpenses(branchIds) {
      if (!branchIds || !branchIds.length === 0 || this.expenses.length > 0) return null;

      this.itemsLoading = true;

      const params = {
        branchIds: branchIds.join(','),
      };

      return this.$http.get('/portal/expenses', { params })
        .then(({ data }) => {
          this.expenses = data.items.map(item => ({
            ids: item.ids,
            type: item.type,
            name: item.name,
            customName: null,
            active: false,
          }));
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.itemsLoading = false;
        });
    },
    loadProfessionals(branchIds) {
      if (!branchIds || !branchIds.length === 0 || this.professionals.length > 0) return null;

      this.itemsLoading = true;

      const params = {
        branchIds: branchIds.join(','),
      };

      return this.$http.get('/portal/professionals', { params })
        .then(({ data }) => {
          this.professionals = data.items.map(item => ({
            id: item.id,
            name: item.name,
            council: item.council,
            specialty: item.specialty,
            active: false,
          }));
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => {
          this.itemsLoading = false;
        });
    },
    async loadItems() {
      const branchIds = this.branches
        .filter(({ active }) => active)
        .map(({ id }) => id);

      await Promise.all([
        this.loadInsurances(branchIds),
        this.loadExpenses(branchIds),
        this.loadProfessionals(branchIds),
      ]);

      this.setItems();
    },
    setItems() {
      this.data.professionals.forEach((item) => {
        const found = this.professionals.find(({ id }) => id === item.id);
        if (found) found.active = true;
      });
      this.data.expenses.forEach((item) => {
        const found = this.expenses
          .find(({ ids }) => ids.some(
            id => item.ids.some(i => String(i) === String(id)),
          ));

        if (found) {
          found.active = true;
          found.customName = item.name;
        }
      });
      this.data.insurances.forEach((item) => {
        const found = this.insurances
          .find(({ planIds }) => planIds.some(
            id => item.planIds.some(i => String(i) === String(id)),
          ));

        if (found) {
          found.active = true;
          found.customName = item.name;
        }
      });
    },
    changeBranchStatus() {
      this.insurances = [];
      this.expenses = [];
      this.professionals = [];
      this.loadItems();
    },
    formatSlugBlur() {
      this.data.slug = (this.data.slug ?? '')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
    },
    save() {
      this.$v.data.$touch();

      if (!this.$v.data.features) {
        this.$toast.error('Selecione ao menos um recurso do portal.');
        return false;
      }

      if (this.$v.data.$error) {
        this.$toast.error('Campo obrigatório.');
        return false;
      }

      const branches = this.branches
        .filter(({ active }) => active)
        .map(item => ({ id: item.id, name: item.name }));

      const insurances = this.insurances
        .filter(({ active }) => active)
        .map(item => ({
          ids: item.ids,
          planIds: item.planIds,
          name: item.name,
          customName: item.customName || item.name,
        }));

      const expenses = this.expenses
        .filter(({ active }) => active)
        .map(item => ({
          ids: item.ids,
          type: item.type,
          name: item.name,
          customName: item.customName || item.name,
        }));

      const professionals = this.professionals
        .filter(({ active }) => active);

      if (branches.length === 0) {
        this.$toast.error('Informe uma ou mais unidades!');
        this.$refs['portal-tabs'].selectTab('tab-branches');
        return null;
      }

      if (this.data.schedulingEnabled) {
        if (insurances.length === 0) {
          this.$toast.error('Informe um ou mais planos!');
          this.$refs['portal-tabs'].selectTab('tab-insurances');
          return null;
        }

        if (expenses.length === 0) {
          this.$toast.error('Informe uma ou mais consultas!');
          this.$refs['portal-tabs'].selectTab('tab-expenses');
          return null;
        }

        if (professionals.length === 0) {
          this.$toast.error('Informe um ou mais profissionais!');
          this.$refs['portal-tabs'].selectTab('tab-professionals');
          return null;
        }
      }

      this.saving = true;

      const payload = {
        slug: this.data.slug,
        themeColor: this.data.themeColor,
        schedulingEnabled: this.data.schedulingEnabled,
        resultsEnabled: this.data.resultsEnabled,
        branches,
        expenses,
        insurances,
        professionals,
      };

      return this.$http.post('/portal/identity', payload)
        .then(({ data }) => {
          this.data.id = data.id;
          this.savedSlug = data.slug;
          this.$toast.success('Registro salvo.');
        })
        .catch((e) => {
          if (e.response.data.message === 'Slug already exists') {
            this.$toast.error('Este slug está em uso por outro cliente.');
          } else {
            this.$toast.error('Ocorreu um erro ao salvar.');
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    remove() {
      this.deleting = true;

      this.$http.delete('/portal/identity')
        .then(() => {
          this.data = this.blankData();
          this.savedSlug = null;
          this.isActive = false;
          this.$toast.success('Portal excluído.');
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao excluir.');
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    copyUrl(url) {
      navigator.clipboard.writeText(url);
      this.$toast.success('Endereço copiado.');
    },
    changeInsuranceOrExpenseStatus(item) {
      item.customName = item.active ? item.name : null;
    },
    async loadAppointments() {
      try {
        this.appointmentLoading = true;

        const params = {
          date: moment(`${this.filter.appointmentYear}-${this.filter.appointmentMonth}-01`).toDate(),
        };

        const { data } = await this.$http.get('/portal/reports', { params });
        this.appointments = data.items;
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.appointmentLoading = false;
      }
    },
    async loadStats() {
      try {
        this.statsLoading = true;

        const params = {
          date: moment(`${this.filter.statsYear}-${this.filter.statsMonth}-01`).toDate(),
        };

        const { data } = await this.$http.get('/portal/stats', { params });

        this.charts.appointments.data = {
          labels: data.appointmentsByDay
            .map(({ label }) => moment(label).format('DD/MM/YY')),
          datasets: [
            {
              type: 'line',
              label: 'Agendamentos',
              data: data.appointmentsByDay.map(({ value }) => value),
              backgroundColor: 'rgb(248,159,85)',
              borderColor: 'rgb(248,159,85)',
              borderWidth: 2,
              fill: false,
            },
            {
              type: 'bar',
              label: 'Visitas',
              data: data.visitsByDay.map(({ value }) => value),
              backgroundColor: 'rgba(0,170,204,0.5)',
              borderColor: 'rgba(0,170,204,1)',
              borderWidth: 1,
              fill: false,
            },
          ],
        };

        this.charts.insurances.data = {
          labels: data.appointmentsByInsurance.map(({ label }) => label),
          datasets: [{
            label: 'Quantidade',
            data: data.appointmentsByInsurance.map(({ value }) => value),
            backgroundColor: [
              'rgba(0, 170, 204, 0.4)',
              'rgba(99, 102, 241, 0.4)',
              'rgba(16, 185, 129, 0.4)',
              'rgba(245, 158, 11, 0.4)',
              'rgba(239, 68, 68, 0.4)',
              'rgba(236, 72, 153, 0.4)',
              'rgba(168, 85, 247, 0.4)',
              'rgba(20, 184, 166, 0.4)',
              'rgba(249, 115, 22, 0.4)',
              'rgba(34, 197, 94, 0.4)',
              'rgba(59, 130, 246, 0.4)',
              'rgba(234, 179, 8, 0.4)',
              'rgba(244, 63, 94, 0.4)',
              'rgba(139, 92, 246, 0.4)',
              'rgba(6, 182, 212, 0.4)',
              'rgba(132, 204, 22, 0.4)',
              'rgba(251, 146, 60, 0.4)',
              'rgba(14, 165, 233, 0.4)',
              'rgba(217, 70, 239, 0.4)',
              'rgba(52, 211, 153, 0.4)',
            ],
            borderColor: [
              'rgba(0, 170, 204, 1)',
              'rgba(99, 102, 241, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(239, 68, 68, 1)',
              'rgba(236, 72, 153, 1)',
              'rgba(168, 85, 247, 1)',
              'rgba(20, 184, 166, 1)',
              'rgba(249, 115, 22, 1)',
              'rgba(34, 197, 94, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(234, 179, 8, 1)',
              'rgba(244, 63, 94, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(6, 182, 212, 1)',
              'rgba(132, 204, 22, 1)',
              'rgba(251, 146, 60, 1)',
              'rgba(14, 165, 233, 1)',
              'rgba(217, 70, 239, 1)',
              'rgba(52, 211, 153, 1)',
            ],
            borderWidth: 1,
          }],
        };

        this.charts.specialties.data = {
          labels: data.appointmentsByExpense.map(({ label }) => label),
          datasets: [{
            label: 'Quantidade',
            data: data.appointmentsByExpense.map(({ value }) => value),
            backgroundColor: [
              'rgba(168, 85, 247, 0.4)',
              'rgba(20, 184, 166, 0.4)',
              'rgba(249, 115, 22, 0.4)',
              'rgba(34, 197, 94, 0.4)',
              'rgba(59, 130, 246, 0.4)',
              'rgba(234, 179, 8, 0.4)',
              'rgba(244, 63, 94, 0.4)',
              'rgba(139, 92, 246, 0.4)',
              'rgba(6, 182, 212, 0.4)',
              'rgba(132, 204, 22, 0.4)',
              'rgba(251, 146, 60, 0.4)',
              'rgba(0, 170, 204, 0.4)',
              'rgba(236, 72, 153, 0.4)',
              'rgba(16, 185, 129, 0.4)',
              'rgba(245, 158, 11, 0.4)',
              'rgba(14, 165, 233, 0.4)',
              'rgba(217, 70, 239, 0.4)',
              'rgba(52, 211, 153, 0.4)',
            ],
            borderColor: [
              'rgba(168, 85, 247, 1)',
              'rgba(20, 184, 166, 1)',
              'rgba(249, 115, 22, 1)',
              'rgba(34, 197, 94, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(234, 179, 8, 1)',
              'rgba(244, 63, 94, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(6, 182, 212, 1)',
              'rgba(236, 72, 153, 1)',
              'rgba(132, 204, 22, 1)',
              'rgba(251, 146, 60, 1)',
              'rgba(0, 170, 204, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(14, 165, 233, 1)',
              'rgba(217, 70, 239, 1)',
              'rgba(52, 211, 153, 1)',
            ],
            borderWidth: 1,
          }],
        };
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.statsLoading = false;
      }
    },
    loadFilters() {
      this.months = moment.months().map((month, index) => ({
        value: String(index + 1).padStart(2, '0'),
        label: moment().month(index).format('MMMM'),
      }));

      const currentYear = moment().year();
      this.years = Array.from({ length: 6 }, (_, i) => {
        const year = currentYear - 5 + i;
        return {
          value: String(year),
          label: String(year),
        };
      });
    },
    chartBase() {
      return {
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
              },
            }],
          },
        },
      };
    },
    blankData() {
      return {
        id: null,
        slug: null,
        themeColor: 'slate',
        schedulingEnabled: false,
        resultsEnabled: false,
        branches: [],
        insurances: [],
        expenses: [],
        professionals: [],
      };
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_variables.scss';
  .portal-page {
    .divider {
      margin: $layout-spacing-lg 0;
    }

    .portal-disable {
      align-items: center;
      display: flex;
      justify-content: space-between;

      .portal-disable-text {
        flex: 1 1 auto;
        margin-right: $layout-spacing-lg;
      }

      .btn {
        flex: 0 0 auto;
      }
    }

    .portal-actions {
      text-align: right;
    }

    .portal-resources {
      .form-input-hint {
        margin-top: $unit-1;
      }

      .portal-resource-link {
        margin-bottom: $layout-spacing;
        max-width: 32rem;

        .portal-resource-link-label {
          color: $gray-color-dark;
          font-size: $font-size-sm;
          margin-bottom: $unit-1;

          svg {
            margin-right: $unit-1;
          }
        }

        .form-input[readonly] {
          background: $bg-color;
        }
      }
    }
  }
</style>
